import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { Checkbox } from '@tecuity/granite-ui/Checkbox'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { DatePicker } from '@tecuity/granite-ui/DatePicker'
import CrossHeavy from '@tecuity/sos-icons/react/cross-heavy'
import Revert from '@tecuity/sos-icons/react/revert'
import RightArrow from '@tecuity/sos-icons/react/right-arrow'
import CalendarIcon from '@tecuity/sos-icons/react/calendar'
import format from 'date-fns/format'
import { Spinner } from '../../Loaders/Loaders'
import { CheckboxList } from '../SettingsViews/SettingsComponents/SelectableCheckboxList'
import { useRequestData } from '../../../Utilities/hooks'
import {
  FilingSourceTypeIds,
  MiscJobDaysOfWeek,
  MiscJobDaysOfMonth,
  MiscJobMonths,
  defaultMiscJobSchedule,
  formatPascalCaseLabel,
} from '../../../Utilities/Utilities'
import * as api from '../../../requests'
import { setErrorToast, setSuccessToast } from '../../../actions'
import { SUBROUTES } from '../../../App'
import './MiscRecordsFilingTypeSettings.scss'

const NO_CHANGE_SENTINEL = '__no_change__'

const defaultDto = {
  MiscellaneousFilingTypeId: null,
  MiscellaneousModuleId: null,
  Name: '',
  IsInitialFiling: false,
  IsJobFiling: false,
  IsActive: true,
  ShowInPublicHistory: false,
  ImagePageTypeId: null,
  FormDefinitionId: null,
  RecordAccessRolesPermittedToFile: [],
  RequirementsToFileJson: null,
  ResultsOfApprovedFilingJson: null,
  NewRecordAccessRole: null,
  AllowedConcurrentFilingsNum: null,
  IncludeImageForPaperFiling: false,
  FrequencyType: null,
  ScheduleJson: null,
  NextRunDate: null,
  LastRunDate: null,
  LastModifiedByUserId: null,
  LastModifiedByUser: '',
  LastModifiedByDate: null,
  GeneratedReportIds: [],
  GeneratedEmailKeys: [],
}

const MiscRecordsFilingTypeSettings = () => {
  const dispatch = useDispatch()
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const readOnly = !appUserRights.includes('CanConfigureMiscellaneousModules')

  const allModules = (SUBROUTES || [])
    .filter((r) => r.IsMiscRecordsType)
    .flatMap((r) => r.MiscRecordsModules || [])
    .sort((a, b) => a.DisplayName.localeCompare(b.DisplayName))

  const moduleOptions = allModules.map((m) => ({
    value: m.MiscellaneousModuleId,
    label: m.DisplayName,
  }))

  const imagePageTypes = useRequestData(api.GET_ALL_IMAGE_PAGE_TYPES, [], [])
  const generatedReportOptions = useRequestData(
    (cb, errCb) => api.GET_APP_REPORTS_BY_CATEGORY_SOURCE_TYPE_ID(FilingSourceTypeIds.Miscellaneous, cb, errCb),
    [],
    [],
  )
  const generatedEmailOptions = useRequestData(api.GET_MISC_GENERATED_EMAIL_OPTIONS, [], [])

  const frequencyTypeKeys = useRequestData(api.GET_MISC_JOB_FREQUENCY_TYPES, [], [])
  const frequencyTypeOptions = useMemo(
    () => (frequencyTypeKeys || []).map((name) => ({
      value: name,
      label: formatPascalCaseLabel(name),
    })),
    [frequencyTypeKeys],
  )

  const [selectedModuleId, setSelectedModuleId] = useState(null)
  const [filingTypeList, setFilingTypeList] = useState([])
  const [activeFilingTypeId, setActiveFilingTypeId] = useState(0)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [filingType, setFilingType] = useState({ ...defaultDto })
  const [originalFilingType, setOriginalFilingType] = useState({ ...defaultDto })
  const [isDirty, setIsDirty] = useState(false)
  const [recordAccessUserRoles, setRecordAccessUserRoles] = useState([])
  const [rolesLoading, setRolesLoading] = useState(false)
  const [fieldsLoading, setFieldsLoading] = useState(false)
  const [formsLoading, setFormsLoading] = useState(false)
  const moduleDataLoading = rolesLoading || fieldsLoading || formsLoading
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [filingFields, setFilingFields] = useState([])
  const [formData, setFormData] = useState([])
  const [requirementsState, setRequirementsState] = useState({})
  const [resultsState, setResultsState] = useState({})
  const [scheduleState, setScheduleState] = useState({ ...defaultMiscJobSchedule })
  const [nextRunDateOpen, setNextRunDateOpen] = useState(false)
  const [nextRunDateText, setNextRunDateText] = useState('')
  const [loadingSave, setLoadingSave] = useState(false)

  useEffect(() => {
    if (allModules.length > 0) {
      setSelectedModuleId(allModules[0].MiscellaneousModuleId)
    }
  }, [])

  useEffect(() => {
    if (!selectedModuleId) return
    let ignore = false
    setFilingTypeList([])
    setActiveFilingTypeId(0)
    setIsAddingNew(false)
    setFilterText('')
    setFilingType({ ...defaultDto })
    setOriginalFilingType({ ...defaultDto })
    setIsDirty(false)
    setFilingFields([])
    setRequirementsState({})
    setResultsState({})
    setIsLoading(true)
    api.GET_MISC_RECORDS_FILING_TYPES_BY_MODULE(
      selectedModuleId,
      (res) => {
        if (ignore) return
        setFilingTypeList(Array.isArray(res) ? res : [])
        setIsLoading(false)
      },
      () => {
        if (ignore) return
        dispatch(setErrorToast('Failed to load filing types.'))
        setIsLoading(false)
      },
    )
    return () => { ignore = true }
  }, [selectedModuleId])

  useEffect(() => {
    if (!selectedModuleId) return
    let ignore = false
    setRolesLoading(true)
    api.GET_MISC_RECORD_ACCESS_ROLES_BY_MODULE(
      selectedModuleId,
      (res) => {
        if (ignore) return
        setRecordAccessUserRoles(Array.isArray(res) ? res : [])
        setRolesLoading(false)
      },
      () => {
        if (ignore) return
        setRecordAccessUserRoles([])
        setRolesLoading(false)
      },
    )
    return () => { ignore = true }
  }, [selectedModuleId])

  useEffect(() => {
    if (!selectedModuleId) return
    let ignore = false
    setFieldsLoading(true)
    api.GET_MISC_RECORDS_FILING_TYPE_FIELD_DETAILS(
      selectedModuleId,
      (res) => {
        if (ignore) return
        setFilingFields(Array.isArray(res) ? res : [])
        setFieldsLoading(false)
      },
      () => {
        if (ignore) return
        dispatch(setErrorToast('Failed to load field definitions.'))
        setFieldsLoading(false)
      },
    )
    return () => { ignore = true }
  }, [selectedModuleId])

  useEffect(() => {
    if (!selectedModuleId) return
    let ignore = false
    setFormsLoading(true)
    setFormData([])
    api.GET_MISC_SETTINGS_FORMS(
      selectedModuleId,
      (res) => {
        if (ignore) return
        const allForms = [
          ...((res.Categories || []).flatMap((c) => c.Forms || [])),
          ...(res.UncategorizedForms || []),
        ]
        setFormData(allForms)
        setFormsLoading(false)
      },
      () => {
        if (ignore) return
        dispatch(setErrorToast('Failed to load forms.'))
        setFormsLoading(false)
      },
    )
    return () => { ignore = true }
  }, [selectedModuleId])

  useEffect(() => {
    if (isAddingNew) {
      const newDto = { ...defaultDto, MiscellaneousModuleId: selectedModuleId }
      setFilingType(newDto)
      setOriginalFilingType(newDto)
      setIsDirty(false)
      setRequirementsState({})
      setResultsState({})
      setScheduleState({ ...defaultMiscJobSchedule })
      return
    }
    if (!activeFilingTypeId) return
    setLoadingDetail(true)
    api.GET_MISC_RECORDS_FILING_TYPE_BY_ID(
      activeFilingTypeId,
      (res) => {
        setFilingType({ ...res })
        setOriginalFilingType({ ...res })
        setIsDirty(false)
        setLoadingDetail(false)
        setRequirementsState(res.RequirementsToFileJson ? JSON.parse(res.RequirementsToFileJson) : {})
        setResultsState(res.ResultsOfApprovedFilingJson ? JSON.parse(res.ResultsOfApprovedFilingJson) : {})
        setScheduleState(res.ScheduleJson ? JSON.parse(res.ScheduleJson) : { ...defaultMiscJobSchedule })
      },
      () => {
        dispatch(setErrorToast('Failed to load filing type details.'))
        setLoadingDetail(false)
      },
    )
  }, [activeFilingTypeId, isAddingNew])

  useEffect(() => {
    setNextRunDateText(
      filingType.NextRunDate ? format(new Date(filingType.NextRunDate), 'MM/dd/yyyy') : '',
    )
  }, [filingType.NextRunDate])

  const changeValue = (property, value) => {
    setFilingType((prev) => ({ ...prev, [property]: value }))
    setIsDirty(true)
  }

  const changeRequirementValue = (fieldName, value) => {
    setRequirementsState((prev) => ({ ...prev, [fieldName]: value }))
    setIsDirty(true)
  }

  const changeScheduleValue = (fieldName, value) => {
    setScheduleState((prev) => ({ ...prev, [fieldName]: value }))
    setIsDirty(true)
  }

  const mergeScheduleValues = (partial) => {
    setScheduleState((prev) => ({ ...prev, ...partial }))
    setIsDirty(true)
  }

  const changeResultValue = (fieldName, value) => {
    setResultsState((prev) => {
      if (value === NO_CHANGE_SENTINEL) {
        const next = { ...prev }
        delete next[fieldName]
        return next
      }
      return { ...prev, [fieldName]: value }
    })
    setIsDirty(true)
  }

  const clickSave = () => {
    const dto = {
      MiscellaneousModuleId: filingType.MiscellaneousModuleId != null ? filingType.MiscellaneousModuleId : selectedModuleId,
      Name: filingType.Name,
      IsInitialFiling: filingType.IsInitialFiling,
      IsJobFiling: filingType.IsJobFiling,
      IsActive: filingType.IsActive,
      ShowInPublicHistory: filingType.ShowInPublicHistory,
      ImagePageTypeId: filingType.ImagePageTypeId,
      FormDefinitionId: filingType.FormDefinitionId,
      RecordAccessRolesPermittedToFile: filingType.RecordAccessRolesPermittedToFile,
      RequirementsToFileJson: JSON.stringify(requirementsState),
      ResultsOfApprovedFilingJson: JSON.stringify(resultsState),
      AllowedConcurrentFilingsNum: filingType.AllowedConcurrentFilingsNum || null,
      NewRecordAccessRole: filingType.NewRecordAccessRole,
      IncludeImageForPaperFiling: filingType.IncludeImageForPaperFiling,
      FrequencyType: filingType.IsJobFiling ? filingType.FrequencyType : null,
      ScheduleJson: filingType.IsJobFiling ? JSON.stringify(scheduleState) : null,
      NextRunDate: filingType.IsJobFiling ? filingType.NextRunDate : null,
      GeneratedReportIds: filingType.GeneratedReportIds || [],
      GeneratedEmailKeys: filingType.GeneratedEmailKeys || [],
    }
    setLoadingSave(true)
    const isNew = filingType.MiscellaneousFilingTypeId === null
    const onSuccess = (res) => {
      dispatch(setSuccessToast('Filing type saved successfully.'))
      setFilingType({ ...res })
      setOriginalFilingType({ ...res })
      setIsDirty(false)
      setLoadingSave(false)
      if (isNew) {
        setIsAddingNew(false)
        setActiveFilingTypeId(res.MiscellaneousFilingTypeId)
      }
      api.GET_MISC_RECORDS_FILING_TYPES_BY_MODULE(
        selectedModuleId,
        (list) => setFilingTypeList(Array.isArray(list) ? list : []),
        () => {},
      )
    }
    const onError = (err) => {
      if (err && err.DisplayToUser === true && (err.Message || '') !== '') {
        dispatch(setErrorToast(err.Message))
      } else {
        dispatch(setErrorToast('Failed to save filing type.'))
      }
      setLoadingSave(false)
    }
    if (isNew) {
      api.CREATE_MISC_RECORDS_FILING_TYPE(dto, onSuccess, onError)
    } else {
      api.UPDATE_MISC_RECORDS_FILING_TYPE(filingType.MiscellaneousFilingTypeId, dto, onSuccess, onError)
    }
  }

  const clickRevert = () => {
    setFilingType({ ...originalFilingType })
    setRequirementsState(
      originalFilingType.RequirementsToFileJson
        ? JSON.parse(originalFilingType.RequirementsToFileJson)
        : {},
    )
    setResultsState(
      originalFilingType.ResultsOfApprovedFilingJson
        ? JSON.parse(originalFilingType.ResultsOfApprovedFilingJson)
        : {},
    )
    setScheduleState(
      originalFilingType.ScheduleJson
        ? JSON.parse(originalFilingType.ScheduleJson)
        : { ...defaultMiscJobSchedule },
    )
    setIsDirty(false)
  }

  const requirementFields = filingFields
    .filter((f) => f.ConfigurableAsFilingRequirement)
    .sort((a, b) => a.FieldDisplayName.localeCompare(b.FieldDisplayName))

  const resultFields = filingFields
    .filter((f) => f.ConfigurableAsFilingResult)
    .sort((a, b) => a.FieldDisplayName.localeCompare(b.FieldDisplayName))

  const handleAddNew = () => {
    setActiveFilingTypeId(0)
    setIsAddingNew(true)
  }

  const handleSelectFilingType = (id) => {
    setActiveFilingTypeId(id)
    setIsAddingNew(false)
  }

  const filteredList = filingTypeList.filter((ft) =>
    ft.Name.toLowerCase().includes(filterText.toLowerCase()),
  )
  const userFilings = filteredList.filter((ft) => !ft.IsJobFiling)
  const jobFilings = filteredList.filter((ft) => ft.IsJobFiling)

  const nextRunDateObj = filingType.NextRunDate ? new Date(filingType.NextRunDate) : null
  const nextRunDatePart = nextRunDateObj ? format(nextRunDateObj, 'yyyy-MM-dd') : ''
  const nextRunTimePart = nextRunDateObj ? format(nextRunDateObj, 'HH:mm') : ''
  const setNextRunDateParts = (datePart, timePart) => {
    if (!datePart && !timePart) {
      changeValue('NextRunDate', null)
      return
    }
    const date = datePart || format(new Date(), 'yyyy-MM-dd')
    const time = timePart || '00:00'
    changeValue('NextRunDate', `${date}T${time}:00`)
  }
  const handleNextRunDateText = (text) => {
    setNextRunDateText(text)
    if (text === '') {
      setNextRunDateParts('', nextRunTimePart)
    } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(text)) {
      const parsed = new Date(text)
      if (!Number.isNaN(parsed.getTime())) {
        setNextRunDateParts(format(parsed, 'yyyy-MM-dd'), nextRunTimePart)
      }
    }
  }
  return (
    <div className="misc-records-filing-type-settings">
      <h2>Miscellaneous Filing Types</h2>
      <div className="misc-records-filing-type-settings__content">
        <div className="misc-records-filing-type-settings__list-panel">
          <SelectBox
            label="Module"
            noPlaceholder
            value={selectedModuleId || ''}
            onChange={(val) => setSelectedModuleId(+val)}
            options={moduleOptions}
          />
          <TextInput
            customCss={`input {border: none}`}
            placeholder="Filter"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <div className="misc-records-filing-type-settings__list">
            <div className="misc-records-filing-type-settings__list-scroll">
              {isLoading ? (
                <Spinner />
              ) : filteredList.length === 0 ? (
                <div className="misc-records-filing-type-settings__empty">
                  No filing types configured.
                </div>
              ) : (
                <>
                  {userFilings.length > 0 && (
                    <>
                      <div className="misc-records-filing-type-settings__list-group-heading">
                        User Filings
                      </div>
                      {userFilings.map((ft) => (
                        <div
                          key={ft.MiscellaneousFilingTypeId}
                          className={`misc-records-filing-type-settings__list-item${
                            activeFilingTypeId === ft.MiscellaneousFilingTypeId ? ' selected' : ''
                          }`}
                          onClick={() => handleSelectFilingType(ft.MiscellaneousFilingTypeId)}
                        >
                          <span>{ft.Name}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {jobFilings.length > 0 && (
                    <>
                      <div className="misc-records-filing-type-settings__list-group-heading">
                        Job Filings
                      </div>
                      {jobFilings.map((ft) => (
                        <div
                          key={ft.MiscellaneousFilingTypeId}
                          className={`misc-records-filing-type-settings__list-item${
                            activeFilingTypeId === ft.MiscellaneousFilingTypeId ? ' selected' : ''
                          }`}
                          onClick={() => handleSelectFilingType(ft.MiscellaneousFilingTypeId)}
                        >
                          <span>{ft.Name}</span>
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
            {!readOnly && (
              <ContextButton variant="secondary" onClick={handleAddNew}>
                <CrossHeavy />
              </ContextButton>
            )}
          </div>
        </div>

        <div className="misc-records-filing-type-settings__form-panel">
          {moduleDataLoading || loadingDetail ? (
            <Spinner size="large" themeColor="dark-neutral" />
          ) : (
            <div className="misc-records-filing-type-settings__form properties-list scrollable">
                {/* Details */}
                <h3
                  style={{
                    textDecoration: 'underline',
                    display: 'flex',
                    alignSelf: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  Details
                </h3>

                {filingType.MiscellaneousFilingTypeId && (
                  <div className="misc-records-filing-type-settings__metadata">
                    <div>
                      Filing Type Id: <strong>{filingType.MiscellaneousFilingTypeId}</strong>
                    </div>
                    {filingType.LastModifiedByUser && (
                      <div>
                        Last Modified By: <strong>{filingType.LastModifiedByUser}</strong>
                      </div>
                    )}
                    {filingType.LastModifiedByDate && (
                      <div>
                        Last Modified Date: <strong>{filingType.LastModifiedByDate}</strong>
                      </div>
                    )}
                  </div>
                )}

                <TextInput
                  label="Name"
                  value={filingType.Name || ''}
                  onChange={(e) => changeValue('Name', e.target.value)}
                  disabled={readOnly}
                />
                <Checkbox
                  label="Is Initial Filing"
                  checked={filingType.IsInitialFiling}
                  onChange={(checked) => changeValue('IsInitialFiling', checked)}
                  disabled={readOnly}
                />
                <Checkbox
                  label="Is Job Filing"
                  checked={filingType.IsJobFiling}
                  onChange={(checked) => changeValue('IsJobFiling', checked)}
                  disabled={readOnly}
                />

                <Checkbox
                  label="Is Active"
                  checked={filingType.IsActive}
                  onChange={(checked) => changeValue('IsActive', checked)}
                  disabled={readOnly}
                />
                <Checkbox
                  label="Show In Public History"
                  checked={filingType.ShowInPublicHistory}
                  onChange={(checked) => changeValue('ShowInPublicHistory', checked)}
                  disabled={readOnly}
                />
                <SelectBox
                  label="Image Page Type"
                  options={imagePageTypes
                    .map((ipt) => ({ label: ipt.Name, value: ipt.Id }))
                    .sort((a, b) => (a.label > b.label ? 1 : -1))}
                  value={filingType.ImagePageTypeId || ''}
                  onChange={(val) => changeValue('ImagePageTypeId', val === '' ? null : +val)}
                  disabled={readOnly}
                />
                <SelectBox
                  label="Form Title"
                  options={formData
                    .map((f) => ({ label: f.Title, value: f.FormDefinitionId }))
                    .sort((a, b) => (a.label > b.label ? 1 : -1))}
                  value={filingType.FormDefinitionId || ''}
                  onChange={(val) => changeValue('FormDefinitionId', val === '' ? null : +val)}
                  disabled={readOnly}
                />

                {filingType.IsJobFiling && (
                  <>
                    <h3
                      style={{
                        textDecoration: 'underline',
                        display: 'flex',
                        alignSelf: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      Job Configuration
                    </h3>

                    <SelectBox
                      label="Frequency Type"
                      options={frequencyTypeOptions}
                      value={filingType.FrequencyType || ''}
                      onChange={(val) => {
                        changeValue('FrequencyType', val || null)
                        setScheduleState({ ...defaultMiscJobSchedule })
                        setIsDirty(true)
                      }}
                      disabled={readOnly}
                    />

                    {filingType.FrequencyType === 'MinuteInterval' && (
                      <TextInput
                        label="Minute Interval"
                        type="number"
                        min={1}
                        value={scheduleState.MinuteInterval != null ? scheduleState.MinuteInterval : ''}
                        onChange={(e) =>
                          changeScheduleValue(
                            'MinuteInterval',
                            e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10)),
                          )
                        }
                        disabled={readOnly}
                      />
                    )}

                    {filingType.FrequencyType === 'Daily' && (
                      <TextInput
                        label="Time of Day"
                        type="time"
                        value={scheduleState.TimeOfDay || ''}
                        onChange={(e) => changeScheduleValue('TimeOfDay', e.target.value || null)}
                        disabled={readOnly}
                      />
                    )}

                    {filingType.FrequencyType === 'Weekly' && (
                      <>
                        <TextInput
                          label="Time of Day"
                          type="time"
                          value={scheduleState.TimeOfDay || ''}
                          onChange={(e) => changeScheduleValue('TimeOfDay', e.target.value || null)}
                          disabled={readOnly}
                        />
                        <SelectBox
                          label="Day of Week"
                          options={MiscJobDaysOfWeek}
                          value={scheduleState.DayOfWeek != null ? scheduleState.DayOfWeek : ''}
                          onChange={(val) => changeScheduleValue('DayOfWeek', val === '' ? null : +val)}
                          disabled={readOnly}
                        />
                      </>
                    )}

                    {filingType.FrequencyType === 'Monthly' && (
                      <>
                        <TextInput
                          label="Time of Day"
                          type="time"
                          value={scheduleState.TimeOfDay || ''}
                          onChange={(e) => changeScheduleValue('TimeOfDay', e.target.value || null)}
                          disabled={readOnly}
                        />
                        <SelectBox
                          label="Day of Month"
                          options={MiscJobDaysOfMonth}
                          value={scheduleState.IsLastDayOfMonth ? 'last' : (scheduleState.DayOfMonth || '')}
                          onChange={(val) => {
                            if (val === 'last') {
                              mergeScheduleValues({ DayOfMonth: null, IsLastDayOfMonth: true })
                            } else {
                              mergeScheduleValues({
                                DayOfMonth: val === '' ? null : +val,
                                IsLastDayOfMonth: false,
                              })
                            }
                          }}
                          disabled={readOnly}
                        />
                      </>
                    )}

                    {filingType.FrequencyType === 'Yearly' && (
                      <>
                        <TextInput
                          label="Time of Day"
                          type="time"
                          value={scheduleState.TimeOfDay || ''}
                          onChange={(e) => changeScheduleValue('TimeOfDay', e.target.value || null)}
                          disabled={readOnly}
                        />
                        <SelectBox
                          label="Month"
                          options={MiscJobMonths}
                          value={scheduleState.Month || ''}
                          onChange={(val) => changeScheduleValue('Month', val === '' ? null : +val)}
                          disabled={readOnly}
                        />
                        <SelectBox
                          label="Day"
                          options={MiscJobDaysOfMonth.filter((d) => d.value !== 'last')}
                          value={scheduleState.DayOfMonth || ''}
                          onChange={(val) => changeScheduleValue('DayOfMonth', val === '' ? null : +val)}
                          disabled={readOnly}
                        />
                      </>
                    )}

                    <div className="misc-records-filing-type-settings__run-date">
                      <div className="date-picker-holder misc-records-filing-type-settings__next-run-date">
                        <TextInput
                          label="Next Run Date"
                          aria-label="Next Run Date String Input"
                          placeholder="Not scheduled MM/DD/YYYY"
                          value={nextRunDateText}
                          onChange={(e) => handleNextRunDateText(e.target.value)}
                          disabled={readOnly}
                          customCss={{ flexGrow: '1' }}
                        />
                        <TextInput
                          aria-label="Next Run Time"
                          type="time"
                          value={nextRunTimePart}
                          onChange={(e) => setNextRunDateParts(nextRunDatePart, e.target.value)}
                          disabled={readOnly}
                        />
                        <button
                          type="button"
                          className="btn btn-primary btn-raised picker-button"
                          style={{ minWidth: 'fit-content', padding: '0px 7px' }}
                          aria-label="Select Todays Date"
                          onClick={() =>
                            setNextRunDateParts(format(new Date(), 'yyyy-MM-dd'), nextRunTimePart)
                          }
                          disabled={readOnly}
                        >
                          Today
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-raised picker-button"
                          aria-label="Open Next Run Date widget"
                          onClick={() => setNextRunDateOpen(true)}
                          disabled={readOnly}
                        >
                          <CalendarIcon size="small" />
                        </button>
                        <DatePicker
                          aria-label="Next Run Date Picker"
                          selected={nextRunDateObj}
                          value={nextRunDatePart ? format(nextRunDateObj, 'MM/dd/yyyy') : ''}
                          isOpen={nextRunDateOpen}
                          onCalendarClose={() => setNextRunDateOpen(false)}
                          onChange={(e) => {
                            setNextRunDateParts(format(new Date(e), 'yyyy-MM-dd'), nextRunTimePart)
                            setNextRunDateOpen(false)
                          }}
                          dateFormat="MM/dd/yyyy"
                        />
                      </div>
                    </div>

                    <div className="misc-records-filing-type-settings__run-date">
                      <span className="misc-records-filing-type-settings__run-date-label">
                        Last Run Date
                      </span>
                      <span className="misc-records-filing-type-settings__run-date-value">
                        {filingType.LastRunDate
                          ? format(new Date(filingType.LastRunDate), 'MM/dd/yyyy h:mm aa')
                          : 'Never'}
                      </span>
                    </div>
                  </>
                )}

                {/* Requirements to File */}
                <h3
                  style={{
                    textDecoration: 'underline',
                    display: 'flex',
                    alignSelf: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  Requirements to File
                </h3>

                <CheckboxList
                  label="Record Access Roles Permitted to File (Leave blank to allow open filing)"
                  items={recordAccessUserRoles.length > 0
                    ? recordAccessUserRoles
                        .map((role) => ({ id: role.RecordAccessUserRoleId, label: role.Name }))
                        .sort((a, b) => (a.label > b.label ? 1 : -1))
                    : [{ id: -1, label: 'No Configurable Roles Available' }]}
                  checkedIds={filingType.RecordAccessRolesPermittedToFile || []}
                  setChecked={(ids) => changeValue('RecordAccessRolesPermittedToFile', ids)}
                  disabled={recordAccessUserRoles.length === 0 || readOnly}
                />
                <TextInput
                  label="Allowed Concurrent Filings"
                  type="number"
                  min={0}
                  value={filingType.AllowedConcurrentFilingsNum != null ? filingType.AllowedConcurrentFilingsNum : ''}
                  onChange={(e) =>
                    changeValue(
                      'AllowedConcurrentFilingsNum',
                      e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10)),
                    )
                  }
                  disabled={readOnly}
                />
                {requirementFields.map((field) => {
                  const fieldKey = field.FieldName
                  const currentValue = requirementsState[fieldKey]
                  if (+field.DataType === 3 || +field.DataType === 4) {
                    // Date or DateTime — allow days before
                    return (
                      <TextInput
                        key={fieldKey}
                        label={`Allow days before ${field.FieldDisplayName}`}
                        type="number"
                        value={currentValue != null ? currentValue : ''}
                        onChange={(e) =>
                          changeRequirementValue(
                            fieldKey,
                            e.target.value === '' ? null : parseInt(e.target.value, 10),
                          )
                        }
                        disabled={readOnly}
                      />
                    )
                  }
                  if (+field.DataType === 2) {
                    return (
                      <SelectBox
                        key={fieldKey}
                        label={field.FieldDisplayName}
                        options={[
                          { value: 'true', label: 'True' },
                          { value: 'false', label: 'False' },
                          { value: '', label: 'N/A' },
                        ]}
                        value={
                          currentValue === true
                            ? 'true'
                            : currentValue === false
                            ? 'false'
                            : ''
                        }
                        onChange={(val) =>
                          changeRequirementValue(
                            fieldKey,
                            val === 'true' ? true : val === 'false' ? false : null,
                          )
                        }
                        noPlaceholder
                        disabled={readOnly}
                      />
                    )
                  }
                  if (+field.DataType === 5) {
                    const lookupItems = (() => {
                      try {
                        return JSON.parse(field.LookupTypeValuesJson || '[]')
                      } catch {
                        return []
                      }
                    })().map((item) => ({ id: item.value, label: item.label }))
                    return (
                      <CheckboxList
                        key={fieldKey}
                        label={`${field.FieldDisplayName} (Leave blank to allow open filing)`}
                        items={lookupItems}
                        checkedIds={Array.isArray(currentValue) ? currentValue : []}
                        setChecked={(ids) => changeRequirementValue(fieldKey, ids)}
                        disabled={readOnly}
                      />
                    )
                  }
                  return null
                })}

                {/* Results of Approved Filing */}
                <h3
                  style={{
                    textDecoration: 'underline',
                    display: 'flex',
                    alignSelf: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  Results of Approved Filing
                </h3>

                {resultFields.map((field) => {
                  const fieldKey = field.FieldName
                  const rawValue = resultsState[fieldKey]
                  if (+field.DataType === 3 || +field.DataType === 4) {
                    // Date or DateTime — three-tier: No Change / Null / Increment / Set Date
                    const dateVal = typeof rawValue === 'object' && rawValue !== null ? rawValue : {}
                    return (
                      <div key={fieldKey}>
                        <SelectBox
                          label={field.FieldDisplayName}
                          options={[
                            { value: NO_CHANGE_SENTINEL, label: 'No Change' },
                            { value: '__null__', label: 'Null' },
                            { value: 'increment', label: 'Increment' },
                            { value: 'set-date', label: 'Set Date' },
                          ]}
                          value={rawValue === undefined ? NO_CHANGE_SENTINEL : rawValue === null ? '__null__' : (dateVal.action || '')}
                          onChange={(val) => {
                            if (val === NO_CHANGE_SENTINEL) changeResultValue(fieldKey, NO_CHANGE_SENTINEL)
                            else if (val === '__null__') changeResultValue(fieldKey, null)
                            else changeResultValue(fieldKey, { action: val })
                          }}
                          noPlaceholder
                          disabled={readOnly}
                        />
                        {dateVal.action === 'increment' && (
                          <>
                            <SelectBox
                              options={[
                                { value: 'Days', label: 'Days' },
                                { value: 'Months', label: 'Months' },
                                { value: 'Years', label: 'Years' },
                              ]}
                              value={dateVal.incrementUnit || ''}
                              onChange={(val) =>
                                changeResultValue(fieldKey, { ...dateVal, incrementUnit: val })
                              }
                              disabled={readOnly}
                            />
                            <TextInput
                              type="number"
                              value={dateVal.incrementValue != null ? dateVal.incrementValue : ''}
                              onChange={(e) =>
                                changeResultValue(fieldKey, {
                                  ...dateVal,
                                  incrementValue:
                                    e.target.value === '' ? null : parseInt(e.target.value, 10),
                                })
                              }
                              disabled={readOnly}
                            />
                          </>
                        )}
                        {dateVal.action === 'set-date' && (
                          <>
                            <SelectBox
                              options={[
                                { value: 'Processed Date', label: 'Processed Date' },
                                { value: 'Received Date', label: 'Received Date' },
                                { value: 'Specify Date', label: 'Specify Date' },
                              ]}
                              value={dateVal.dateOption || ''}
                              onChange={(val) =>
                                changeResultValue(fieldKey, { action: dateVal.action, dateOption: val })
                              }
                              disabled={readOnly}
                            />
                            {dateVal.dateOption === 'Specify Date' && (
                              <>
                                <SelectBox
                                  label="Day"
                                  options={[
                                    { value: 'Registration Date', label: 'Day From Registration Date' },
                                    { value: 'Processed Date', label: 'Day From Processed Date' },
                                    { value: 'Received Date', label: 'Day From Received Date' },
                                    { value: 'Last Day of Month', label: 'Last Day of Month' },
                                    { value: 'Specify Day', label: 'Specify Day' },
                                  ]}
                                  value={dateVal.dayOption || ''}
                                  onChange={(val) =>
                                    changeResultValue(fieldKey, { ...dateVal, dayOption: val, dayValue: undefined })
                                  }
                                  disabled={readOnly}
                                />
                                {dateVal.dayOption === 'Specify Day' && (
                                  <TextInput
                                    label="Day (1-31)"
                                    type="number"
                                    value={dateVal.dayValue != null ? dateVal.dayValue : ''}
                                    onChange={(e) =>
                                      changeResultValue(fieldKey, {
                                        ...dateVal,
                                        dayValue: e.target.value === '' ? null : parseInt(e.target.value, 10),
                                      })
                                    }
                                    disabled={readOnly}
                                  />
                                )}
                                <SelectBox
                                  label="Month"
                                  options={[
                                    { value: 'Registration Date', label: 'Month From Registration Date' },
                                    { value: 'Processed Date', label: 'Month From Processed Date' },
                                    { value: 'Received Date', label: 'Month From Received Date' },
                                    { value: 'Specify Month', label: 'Specify Month' },
                                  ]}
                                  value={dateVal.monthOption || ''}
                                  onChange={(val) =>
                                    changeResultValue(fieldKey, { ...dateVal, monthOption: val, monthValue: undefined })
                                  }
                                  disabled={readOnly}
                                />
                                {dateVal.monthOption === 'Specify Month' && (
                                  <TextInput
                                    label="Month (1-12)"
                                    type="number"
                                    value={dateVal.monthValue != null ? dateVal.monthValue : ''}
                                    onChange={(e) =>
                                      changeResultValue(fieldKey, {
                                        ...dateVal,
                                        monthValue: e.target.value === '' ? null : parseInt(e.target.value, 10),
                                      })
                                    }
                                    disabled={readOnly}
                                  />
                                )}
                                <SelectBox
                                  label="Year"
                                  options={[
                                    { value: 'Specify Year', label: 'Specify Year' },
                                    { value: 'Next Occurrence', label: 'Next Occurrence' },
                                  ]}
                                  value={dateVal.yearOption || ''}
                                  onChange={(val) =>
                                    changeResultValue(fieldKey, {
                                      ...dateVal,
                                      yearOption: val,
                                      yearValue: undefined,
                                      yearDateRef: undefined,
                                      nextOccurrenceDateRef: undefined,
                                    })
                                  }
                                  disabled={readOnly}
                                />
                                {dateVal.yearOption === 'Specify Year' && (
                                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                    <TextInput
                                      type="number"
                                      value={dateVal.yearValue != null ? dateVal.yearValue : ''}
                                      onChange={(e) =>
                                        changeResultValue(fieldKey, {
                                          ...dateVal,
                                          yearValue: e.target.value === '' ? null : parseInt(e.target.value, 10),
                                        })
                                      }
                                      disabled={readOnly}
                                    />
                                    <span>Years after</span>
                                    <SelectBox
                                      options={[
                                        { value: 'Registration Date', label: 'Registration Date' },
                                        { value: 'Processed Date', label: 'Processed Date' },
                                        { value: 'Received Date', label: 'Received Date' },
                                      ]}
                                      value={dateVal.yearDateRef || ''}
                                      onChange={(val) =>
                                        changeResultValue(fieldKey, { ...dateVal, yearDateRef: val })
                                      }
                                      disabled={readOnly}
                                    />
                                  </div>
                                )}
                                {dateVal.yearOption === 'Next Occurrence' && (
                                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                    <span>Next Month/Day as configured above after the</span>
                                    <SelectBox
                                      options={[
                                        { value: 'Registration Date', label: 'Registration Date' },
                                        { value: 'Processed Date', label: 'Processed Date' },
                                        { value: 'Received Date', label: 'Received Date' },
                                      ]}
                                      value={dateVal.nextOccurrenceDateRef || ''}
                                      onChange={(val) =>
                                        changeResultValue(fieldKey, { ...dateVal, nextOccurrenceDateRef: val })
                                      }
                                      disabled={readOnly}
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )
                  }
                  if (+field.DataType === 2) {
                    // Bool — No Change / True / False / Null
                    return (
                      <SelectBox
                        key={fieldKey}
                        label={field.FieldDisplayName}
                        options={[
                          { value: NO_CHANGE_SENTINEL, label: 'No Change' },
                          { value: 'true', label: 'True' },
                          { value: 'false', label: 'False' },
                          { value: '__null__', label: 'Null' },
                        ]}
                        value={
                          rawValue === undefined ? NO_CHANGE_SENTINEL : rawValue === true ? 'true' : rawValue === false ? 'false' : '__null__'
                        }
                        onChange={(val) =>
                          changeResultValue(
                            fieldKey,
                            val === 'true' ? true : val === 'false' ? false : val === NO_CHANGE_SENTINEL ? NO_CHANGE_SENTINEL : null,
                          )
                        }
                        noPlaceholder
                        disabled={readOnly}
                      />
                    )
                  }
                  if (+field.DataType === 5) {
                    // Lookup — No Change / Null / lookup options
                    const lookupOptions = (() => {
                      try {
                        return JSON.parse(field.LookupTypeValuesJson || '[]')
                      } catch (e) {
                        return []
                      }
                    })().map((item) => ({ value: item.value, label: item.label }))
                    return (
                      <SelectBox
                        key={fieldKey}
                        label={field.FieldDisplayName}
                        options={[
                          { value: NO_CHANGE_SENTINEL, label: 'No Change' },
                          { value: '__null__', label: 'Null' },
                          ...lookupOptions,
                        ]}
                        value={rawValue === undefined ? NO_CHANGE_SENTINEL : rawValue === null ? '__null__' : rawValue}
                        onChange={(val) => changeResultValue(fieldKey, val === NO_CHANGE_SENTINEL ? NO_CHANGE_SENTINEL : val === '__null__' ? null : val)}
                        noPlaceholder
                        disabled={readOnly}
                      />
                    )
                  }
                  return null
                })}

                <SelectBox
                  label="Record Access Role Granted Post-Approval"
                  options={recordAccessUserRoles
                    .map((role) => ({ label: role.Name, value: role.RecordAccessUserRoleId }))
                    .sort((a, b) => (a.label > b.label ? 1 : -1))}
                  value={filingType.NewRecordAccessRole || ''}
                  onChange={(val) => changeValue('NewRecordAccessRole', val === '' ? null : +val)}
                  disabled={recordAccessUserRoles.length === 0 || readOnly}
                  placeholder={recordAccessUserRoles.length === 0 ? 'No Roles Configured to this Module' : undefined}
                />
                <Checkbox
                  label="Include Original Image with Approval Stamp for Paper Filings"
                  checked={filingType.IncludeImageForPaperFiling}
                  onChange={(checked) => changeValue('IncludeImageForPaperFiling', checked)}
                  disabled={readOnly}
                />
                <CheckboxList
                  label="Generated Reports"
                  items={generatedReportOptions.map((r) => ({ id: r.Id, label: `${r.Category} - ${r.Name}` }))}
                  checkedIds={filingType.GeneratedReportIds || []}
                  setChecked={(ids) => changeValue('GeneratedReportIds', ids)}
                  disabled={readOnly}
                />
                <CheckboxList
                  label="Generated Emails (Ignored For Paper Filings)"
                  items={generatedEmailOptions.map((e) => ({ id: e.Key, label: e.Subject }))}
                  checkedIds={filingType.GeneratedEmailKeys || []}
                  setChecked={(keys) => changeValue('GeneratedEmailKeys', keys)}
                  disabled={readOnly}
                />
                {!readOnly && (
                  <div className="button-wrapper">
                    {loadingSave && (
                      <div className="loading-type">
                        <Spinner size="small" themeColor="dark-neutral" />
                      </div>
                    )}
                    <ContextButton
                      variant="danger"
                      disabled={!isDirty}
                      leftIcon={
                        <div className="checkmark-wrapper left">
                          <Revert />
                        </div>
                      }
                      onClick={clickRevert}
                    >
                      Revert
                    </ContextButton>
                    <ContextButton
                      variant="primary"
                      disabled={!isDirty}
                      rightIcon={
                        <div className="checkmark-wrapper right">
                          <RightArrow />
                        </div>
                      }
                      onClick={clickSave}
                    >
                      {filingType.MiscellaneousFilingTypeId === null ? 'Add' : 'Save'}
                    </ContextButton>
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  )
}

export default MiscRecordsFilingTypeSettings
